import * as React from "react";
import { useNavigate } from "react-router";
import NewCompanyContactPage from "../../pages/NewCompanyContactPage";
import NewPrivateContactPage from "../../pages/NewPrivateContactPage";
import { OpenWindowConfig, useWindowManager } from "./WindowManagerContext";
import type { FormState } from "../../utils/formUtils";
import type { ZusatzPayload } from "../../utils/refZuVorbelegung";

export type ContactFormWindowProps = {
  windowId: string;
  /** "company" → Firmenkontakt-Form, "private" → Privatkontakt-Form. */
  kind: "company" | "private";
  draftN: number;
  /** Vorbelegte Formularfelder (Feldname → Wert), z. B. { emailPrivate } aus
   *  einem Termin-Teilnehmer (Termine-SP8/A17). Zählt nicht als „dirty". */
  initialValues?: Record<string, string>;
  /** Nach erfolgreicher Anlage STATT der Standard-Navigation (/contacts/:id)
   *  aufrufen — z. B. um den Vorgang-Erstell-Flow mit dem neuen Kontakt
   *  fortzusetzen (Termine-SP8/A17). Bekommt den gespeicherten
   *  Formularstand mit (Name, Rechtsform …), damit der Aufrufer den neuen
   *  Kontakt ohne weiteren Abruf anzeigen kann. */
  onCreated?: (contactId: number, felder?: FormState) => void;
  /** Beschriftung des Speichern-Knopfs („Übernehmen", „Weiter"). */
  speichernText?: string;
  /** Bezug zum Elternkontakt für den POST (FO/BO-Vererbung, Haushalt). */
  zusatzPayload?: ZusatzPayload;
  /** Titel „Neuer Privatkontakt für <fuer>" — aus welchem Kontakt heraus. */
  fuer?: string;
};

let contactDraftCounter = 0;

const typeLabel = (kind: "company" | "private", fuer?: string): string =>
  `${kind === "company" ? "Neuer Firmenkontakt" : "Neuer Privatkontakt"}${fuer?.trim() ? ` für ${fuer.trim()}` : ""}`;

/** Baut den Basistitel (Typ-Label + Entwurf-Suffix). */
const buildContactTitleBase = (kind: "company" | "private", draftN: number, fuer?: string): string =>
  `${typeLabel(kind, fuer)} · Entwurf #${draftN}`;

/**
 * Öffnet ein Contact-Form-Window. Titel-Schema:
 * `Neuer {Firmen|Privat}kontakt · {Name|Entwurf #N}`. Sobald der User Name/
 * Firma tippt, aktualisiert die Page den Titel via onTitleSuffix.
 *
 * Keine stabile ID pro Kontakt (Anlage hat keine ID) → Date.now() macht jeden
 * Klick zu einem eigenen Entwurfsfenster.
 */
export const openContactFormWindow = (
  openWindow: (config: OpenWindowConfig) => void,
  params: {
    kind: "company" | "private";
    initialSize?: { width: number; height: number };
    /** Vorbelegte Formularfelder (z. B. { emailPrivate }, Termine-SP8/A17). */
    initialValues?: Record<string, string>;
    /** Nach der Anlage statt /contacts/:id-Navigation (Termine-SP8/A17). */
    onCreated?: (contactId: number, felder?: FormState) => void;
    /** Beschriftung des Speichern-Knopfs (Vollanlage aus der Schnellanlage). */
    speichernText?: string;
    /** Bezug zum Elternkontakt, geht mit dem POST mit. */
    zusatzPayload?: ZusatzPayload;
    /** Titel „Neuer Privatkontakt für <fuer>" — aus welchem Kontakt heraus. */
    fuer?: string;
  },
): string => {
  const { kind, initialSize, initialValues, onCreated, speichernText, zusatzPayload, fuer } = params;
  const draftN = ++contactDraftCounter;
  const title = buildContactTitleBase(kind, draftN, fuer);
  const windowId = `contact-${kind}-new-${Date.now()}`;

  openWindow({
    id: windowId,
    title,
    initialSize: initialSize ?? { width: 1140, height: 820 },
    // Standard: fullpage/maximiert öffnen. Restore fällt auf initialSize zurück.
    initialMaximized: true,
    render: () => (
      <ContactFormWindow
        windowId={windowId}
        kind={kind}
        draftN={draftN}
        initialValues={initialValues}
        onCreated={onCreated}
        speichernText={speichernText}
        zusatzPayload={zusatzPayload}
        fuer={fuer}
      />
    ),
  });
  return windowId;
};

/**
 * Rendert die existierenden Kontakt-Anlage-Pages in einer Window-Card. KEIN
 * MemoryRouter — React Router erlaubt keine verschachtelten Router. Stattdessen
 * erhalten die Pages ein embedded-Prop, das navigate-Aufrufe durch Window-
 * Callbacks ersetzt.
 */
const ContactFormWindow: React.FC<ContactFormWindowProps> = ({
  windowId,
  kind,
  draftN,
  initialValues,
  onCreated,
  speichernText,
  zusatzPayload,
  fuer,
}) => {
  const { closeWindow, setWindowTitle, setWindowDirty } = useWindowManager();
  const navigate = useNavigate();

  const titleBase = React.useMemo(() => buildContactTitleBase(kind, draftN, fuer), [kind, draftN, fuer]);

  const handleSuccess = React.useCallback(
    (createdId: number | null, felder?: FormState) => {
      setWindowDirty(windowId, false); // Guard nach erfolgreichem Save deaktivieren
      closeWindow(windowId);
      // SP8/A17: Aufrufer-Fortsetzung (z. B. Vorgang-Erstell-Flow mit dem
      // neuen Kontakt) statt der Standard-Navigation zur Kontaktakte.
      if (createdId && onCreated) {
        onCreated(createdId, felder);
        return;
      }
      if (createdId) navigate(`/contacts/${createdId}`);
    },
    [closeWindow, navigate, windowId, setWindowDirty, onCreated],
  );

  const handleClose = React.useCallback(() => closeWindow(windowId), [closeWindow, windowId]);

  const handleDirtyChange = React.useCallback(
    (dirty: boolean) => setWindowDirty(windowId, dirty),
    [setWindowDirty, windowId],
  );

  const handleTitleSuffix = React.useCallback(
    (suffix: string | null) => {
      const trimmed = suffix?.trim();
      setWindowTitle(windowId, trimmed ? `${typeLabel(kind, fuer)} · ${trimmed}` : titleBase);
    },
    [setWindowTitle, windowId, kind, titleBase, fuer],
  );

  // Stabile Referenz — wird als Effekt-Dependency der Pages genutzt. Ohne
  // useMemo entstünde bei jedem Window-State-Update (setTitle/setDirty erzeugen
  // ein neues windows-Array) ein neues embedded-Objekt → Page-Effekt feuert →
  // setWindowTitle → neues State → Endlosschleife.
  const embedded = React.useMemo(
    () => ({
      onSuccess: handleSuccess,
      onClose: handleClose,
      onDirtyChange: handleDirtyChange,
      onTitleSuffix: handleTitleSuffix,
      initialValues,
      speichernText,
      zusatzPayload,
    }),
    [handleSuccess, handleClose, handleDirtyChange, handleTitleSuffix, initialValues, speichernText, zusatzPayload],
  );

  if (kind === "company") {
    return <NewCompanyContactPage embedded={embedded} />;
  }
  return <NewPrivateContactPage embedded={embedded} />;
};

export default ContactFormWindow;
